import type { Client } from "@langchain/langgraph-sdk";
import { createAssistantStream } from "assistant-stream";

import { asMetadata } from "@/agents-v2/chat-runtime/thread-metadata";
import type { AgentThreadMetadata } from "@/agents-v2/chat-runtime/types";

/** Longest title a sidebar row shows before it only ellipsizes. */
const TITLE_MAX_LEN = 60;

/**
 * What a history row is called.
 *
 * Two names can exist on a thread and they are not interchangeable:
 *
 * - `title` is the real name — written by the agent at the end of the thread's
 *   first run (`ThreadTitleMiddleware`, backend), or typed by the user.
 * - `thread_name` is Aegra's own stamp of the first human message, written
 *   inside the create-run transaction (`run_preparation.update_thread_metadata`)
 *   before the run is even queued. Nothing a browser does can skip it.
 *
 * Reading through to `thread_name` is what makes a blank row impossible. Even
 * with naming moved to the server a title can be momentarily absent — during
 * the first run, or after a naming attempt that timed out and will retry on the
 * next turn — and a row saying "New chat" while holding a full conversation is
 * the bug this fixes (PE-344). The placeholder belongs only to a thread with
 * nothing in it yet.
 * @param metadata Thread metadata as returned by Aegra.
 * @returns The name to render, or undefined for a thread with neither.
 */
export const displayThreadTitle = (
  metadata: AgentThreadMetadata
): string | undefined => {
  const title = metadata.title?.trim();
  if (title) return title;

  // Aegra allows 100 characters here against our 60, and it is the raw ask
  // rather than a written title, so it is trimmed to match the rows around it.
  const threadName = metadata.thread_name?.trim().replace(/\s+/g, " ");
  if (!threadName) return undefined;
  return threadName.length > TITLE_MAX_LEN
    ? `${threadName.slice(0, TITLE_MAX_LEN - 3)}...`
    : threadName;
};

/**
 * Stream back the name the server gave this thread.
 *
 * assistant-ui calls its title adapter from a one-shot `runEnd` listener in the
 * tab. That event is not a reliable trigger for *generating* a name — our runs
 * outlive the tab (`onDisconnect: "continue"`), so it fires only for a user who
 * stayed, which is exactly how threads ended up unnamed. Generating moved to
 * the agent, and what is left here is the cheap half: the run has just ended,
 * so the agent has already written the name, and this reads it back so the row
 * updates without waiting for the next `list()`.
 *
 * No LLM call and no write, so there is nothing to race against the server's
 * name and nothing to pay for.
 * @param options Where to read from.
 * @param options.client Aegra client.
 * @param options.remoteId Thread id to read.
 * @returns A stream carrying the thread's current display name.
 */
export const createStoredTitleStream = (options: {
  client: Client;
  remoteId: string;
}) =>
  createAssistantStream(async (controller) => {
    const { client, remoteId } = options;
    const thread = await client.threads.get(remoteId);
    const title = displayThreadTitle(asMetadata(thread.metadata));
    if (title) controller.appendText(title);
  });
